import { getPhenotype } from '../../ai2/components/AddNewPatient/utils';
import { removeSpace } from '../../ai2/helpers';
import {
  transformClinicTOToCreatePatientClinic,
  type ClinicTO,
  type CreatePatientClinic,
} from '../transferObjects/Clinic';

export interface EpoccPayload {
  name: 'EPOCC Adult' | 'EPOCC Paed';
  eventDate: string;
  detail: EpoccEventDetail;
}

export type EpoccEventDetail = (AdultEpocc | PaedEpocc) & { phenotype: string };

export interface AdultEpocc {
  epoccDate: string;
  sex: string;
  bpiPainSeverity: string;
  bpiPainInterference: string;
  pcsTotal: string;
  dassTotal: string;
  pseqTotal: string;
}

export interface PaedEpocc {
  epoccDate: string;
  sex: string;
  bpiPainSeverity: string;
  fdi: string;
  bap: string;
  revEmotional: string;
  painDuration: string;
  painCount: '< 14' | '>= 14';
}

export interface AddTenancyPatientPayload {
  username: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  dob: string;
  gender: string;
  medicare: string | null;
  medicareIrn: number | null;
  email?: string;
  postcode: string;
  phone: string | null;
  tenancyId: number;
  clinics: CreatePatientClinic[];
  primaryName?: string;
  primaryRelation?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  events?: EpoccPayload[]; // optional to support MyPATH-like flows
}

export function transformToAddTenancyPatientPayload(
  raw: Record<string, any>,
  tenancyId: number,
  clinics: ClinicTO[],
): AddTenancyPatientPayload {
  const firstName = raw.first_name ?? raw.firstName ?? '';
  const middleName = raw.middle_name ?? raw.middleName ?? null;
  const lastName = raw.last_name ?? raw.lastName ?? '';
  const dob = raw.dob ?? raw.date_of_birth ?? '';
  const gender = raw.gender ?? '';
  const postcode = raw.postcode ?? '';

  const medicareRaw = raw.medicare_no ?? raw.medicareNo ?? null;
  const medicareIrnRaw = raw.medicare_irn ?? raw.medicareIrn ?? null;
  const medicare =
    typeof medicareRaw === 'string' && /^\d{10}$/.test(medicareRaw)
      ? medicareRaw
      : null;
  const medicareIrn =
    medicare &&
    (typeof medicareIrnRaw === 'number' || typeof medicareIrnRaw === 'string')
      ? typeof medicareIrnRaw === 'number'
        ? medicareIrnRaw
        : parseInt(medicareIrnRaw as string, 10) || null
      : null;

  const email = raw.email ?? undefined;

  const primaryName = raw.pc_name ?? raw.primaryName ?? undefined;
  const primaryRelation =
    raw.pc_relationship ??
    raw.primaryRelation ??
    raw.primaryRelationship ??
    undefined;
  const primaryPhone = raw.pc_phone ?? raw.primaryPhone ?? undefined;
  const primaryEmail = raw.pc_email ?? raw.primaryEmail ?? undefined;

  const rawPhone = removeSpace(raw.phone_no ?? raw.phone ?? '');
  const normalizedPhone = normalizeAuPhone(rawPhone);

  // emulate legacy behavior: if phone missing/invalid, fake username and set phone to null
  const useFakie = !normalizedPhone;
  const username = useFakie ? makeFakiePhone() : normalizedPhone;
  const phone = useFakie ? null : normalizedPhone;

  const base: AddTenancyPatientPayload = {
    username,
    firstName,
    middleName,
    lastName,
    dob,
    gender,
    medicare,
    medicareIrn,
    postcode,
    phone,
    tenancyId,
    clinics: transformClinicTOToCreatePatientClinic(clinics),
  };

  if (email) base.email = email;
  if (primaryName) base.primaryName = primaryName;
  if (primaryRelation) base.primaryRelation = primaryRelation;
  if (primaryPhone) base.primaryPhone = primaryPhone;
  if (primaryEmail) base.primaryEmail = primaryEmail;

  // Optional: build EPOCC events if epocc_* fields are present
  const {
    epocc_date,
    epocc_sex,
    epocc_bpi_pain,
    epocc_bpi_interference,
    epocc_pcs_total,
    epocc_dass_total,
    epocc_pseq_total,
    epocc_fdi,
    epocc_bap,
    epocc_rev_emotional,
    epocc_pain_duration,
    epocc_pain_count,
    isPaed,
  } = raw;

  const hasEpocc = Boolean(
    epocc_date || epocc_bpi_pain || epocc_fdi || epocc_pcs_total,
  );
  if (hasEpocc) {
    base.events = base.events || [];
    if (isPaed) {
      const detail: EpoccEventDetail = {
        epoccDate: epocc_date,
        sex: epocc_sex,
        bpiPainSeverity: epocc_bpi_pain || '',
        fdi: epocc_fdi || '',
        bap: epocc_bap || '',
        revEmotional: epocc_rev_emotional || '',
        painDuration: epocc_pain_duration || '',
        painCount: epocc_pain_count === '>= 14' ? '>= 14' : '< 14',
        phenotype: getPhenotype(),
      };
      base.events.push({
        name: 'EPOCC Paed',
        eventDate: epocc_date || new Date().toISOString(),
        detail,
      });
    } else {
      const detail: EpoccEventDetail = {
        epoccDate: epocc_date,
        sex: epocc_sex,
        bpiPainSeverity: epocc_bpi_pain || '',
        bpiPainInterference: epocc_bpi_interference || '',
        pcsTotal: epocc_pcs_total || '',
        dassTotal: epocc_dass_total || '',
        pseqTotal: epocc_pseq_total || '',
        phenotype: getPhenotype(),
      };
      base.events.push({
        name: 'EPOCC Adult',
        eventDate: epocc_date || new Date().toISOString(),
        detail,
      });
    }
  }

  return base;
}

function normalizeAuPhone(raw: string): string {
  const digits = raw.replace(/\D+/g, '');
  if (!digits) return '';
  if (digits.startsWith('0')) {
    return '+61' + digits.slice(1);
  }
  if (digits.startsWith('61')) {
    return '+' + digits;
  }
  if (raw.trim().startsWith('+61')) {
    return '+' + digits;
  }
  if (digits.length === 9 && digits.startsWith('4')) {
    return '+61' + digits;
  }
  return '';
}

function makeFakiePhone(): string {
  const rand = Math.floor(Math.random() * 1_000_000_000)
    .toString()
    .padStart(10, '0');
  return '+378' + rand;
}
